import { useState } from "react";
import { ResultOf } from "gql.tada";
import { motion, useAnimate, useMotionValue } from "motion/react";
import { contentfulClient } from "@utils/contentfulClient";
import { ListSectionById } from "@queries/entries/ListSectionById";
import { ListSectionMoreById } from "@queries/entries/ListSectionMoreById";
import { ListItemFragment } from "@queries/fragments/ListItemFragment";
import { animationCurves } from "@utils/animationCurves";
import { useIsDesktop } from "@hooks/useIsDesktop";
import { useGlobalContext } from "@utils/GlobalContext";
import { Button } from "@components/shared/core/Button";
import ListItem from "./ListItem";

type TListItem = ResultOf<typeof ListItemFragment>;
type ListSectionEntry = ResultOf<typeof ListSectionById>;

export const ListSection = ({ entry }: { entry: ListSectionEntry }) => (
  <ListSectionContent entry={entry} />
);

const ListSectionContent = ({ entry }: { entry: ListSectionEntry }) => {
  const section = entry.listSection;
  const rowHeight = 280;

  const initialItems = (section?.itemsCollection?.items ?? []) as unknown as TListItem[];
  const [listItems, setListItems] = useState<TListItem[]>(initialItems);
  const [isLoading, setIsLoading] = useState(false);
  const [centeredItem, setCenteredItem] = useState<HTMLDivElement | null>(null);
  const [mobileVpObserver] = useState<IntersectionObserver>(
    () =>
      new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) setCenteredItem(e.target as HTMLDivElement);
          });
        },
        { threshold: 1 }
      )
  );

  const barY = useMotionValue(-rowHeight);
  const [scope, animate] = useAnimate();
  const isDesktop = useIsDesktop();
  const { locale } = useGlobalContext();

  if (!section || !listItems.length) return null;

  const entryId = atob(section._id).replace(/^.+\/([^/]+)$/, "$1");
  const hasMoreItems = (section.itemsCollection?.total ?? 0) > listItems.length;

  const handleMouseOver = (idx: number) => {
    animate(barY, rowHeight * idx, animationCurves.macro);
  };

  const handleMouseOut = () => {
    animate(barY, -rowHeight, animationCurves.macro);
  };

  const loadMore = async () => {
    setIsLoading(true);
    try {
      const response = await contentfulClient.query(ListSectionMoreById, {
        id: entryId,
        skip: listItems.length,
      });
      const newItems = response.data?.listSection?.itemsCollection?.items as unknown as TListItem[];
      if (newItems?.length) {
        setListItems([...listItems, ...newItems]);
      }
    } catch (error) {
      console.error("Failed to load more items:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="min-h-screen flex flex-col items-center pb-32 pt-16">
      <div
        ref={scope}
        className="overflow-hidden w-full relative flex flex-col gap-10 dsk:gap-0"
        onMouseLeave={handleMouseOut}
      >
        {isDesktop && (
          <motion.div
            className="absolute w-full bg-palette-current-deep pointer-events-none"
            style={{ height: rowHeight, y: barY }}
          />
        )}

        {listItems.map((item, index) => (
          <ListItem
            key={item?._id || index}
            item={item}
            index={index}
            height={rowHeight}
            centeredItem={centeredItem}
            mobileVpObserver={mobileVpObserver}
            onMouseOver={handleMouseOver}
            locale={locale}
          />
        ))}
      </div>

      {hasMoreItems && (
        <div className="mt-20">
          <Button onClick={loadMore} loading={isLoading}>
            <span>{section.viewMoreText || "View More"}</span>
          </Button>
        </div>
      )}
    </section>
  );
};
